import { DifficultyChip, QuestionTypeChip } from '@exam-management/components';
import {
  type GetTaskQuestionGroupByIdsQuery,
  type GetQuestionsByIdAndQuestionCreationTaskIdQuery,
  WorkflowStepCode,
} from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import {
  Button,
  Chip,
  EditOutlinedIcon,
  EllipsisText,
  Flex,
  styled,
  TableBody,
  TableCell,
  TableRow,
  Text,
} from '@shared/components';
import { Fragment } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useQuestionCreationTaskDetails } from '../../providers';
import { transformQuestionsToQuestionRows } from '../../utils';

interface ItemSetQuestionListTableBodyProps {
  rows: GetTaskQuestionGroupByIdsQuery['qctGetTaskQuestionGroupByIds'];
}

const StyledItemSetTableCell = styled(TableCell)(({ theme }) => ({
  width: '100%',
  p: theme.spacing(3, 9),
  backgroundColor: theme.palette.info.lighter,
  borderLeft: `4px solid ${theme.palette.info.main}`,
}));

const StyledItemSetSubQuestionTableCell = styled(TableCell)(({ theme }) => ({
  borderColor: theme.palette.grey[300],
  minHeight: '48px',
  ...theme.typography.body2Semibold,
}));

export default function ItemSetQuestionListTableBody(
  props: Readonly<ItemSetQuestionListTableBodyProps>,
) {
  const { rows } = props;

  return (
    <TableBody>
      {rows.map((row) => (
        <Fragment key={row.id}>
          <ItemSetQuestionTableRow data={row} questionGroupId={row.id} />
          {row.questions?.map((question, index) => (
            <ItemSetSubQuestionTableRow
              key={question.id}
              data={[question]}
              order={index + 1}
              questionGroupId={row.id}
            />
          ))}
        </Fragment>
      ))}
    </TableBody>
  );
}

function ItemSetQuestionTableRow(
  props: Readonly<{
    data: GetTaskQuestionGroupByIdsQuery['qctGetTaskQuestionGroupByIds'][number];
    questionGroupId: string;
  }>,
) {
  const { data, questionGroupId } = props;
  const { roles, task } = useQuestionCreationTaskDetails();
  const navigate = useNavigate();
  const location = useLocation();
  const matchedTaskManagement = matchPath('/task-management/*', location.pathname);
  const size = task.metadata?.requirement.itemSetCreation?.find(
    (item) => item.questionGroupId === questionGroupId,
  )?.size;
  const isTaskDone = task.currentWorkflowStep.code === WorkflowStepCode.QuestionCreationDone;
  return (
    <TableRow
      onClick={() =>
        navigate(
          matchedTaskManagement
            ? `/task-management/question-creation/:taskId/item-set/:questionId`
            : `/my-task/question-creation/:taskId/item-set/:questionId`,
          {
            params: {
              taskId: task.id,
              questionId: questionGroupId,
            },
            replace: true,
          },
        )
      }
      sx={{ cursor: 'pointer' }}
    >
      <StyledItemSetTableCell
        colSpan={8}
        sx={{ minHeight: '52px', p: (theme) => theme.spacing(3, 4) }}
      >
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Flex sx={{ gap: (theme) => theme.spacing(3), alignItems: 'center' }}>
            <QuestionTypeChip type='item-set' questionSize={size} />
            <Text variant='body1Semibold'>{data.name}</Text>
          </Flex>
          <Flex sx={{ gap: (theme) => theme.spacing(3), alignItems: 'center' }}>
            <Text variant='body2'>{`${data.questions?.length ?? 0}/${size} Sub Questions`}</Text>
            {roles.isAssignee && !isTaskDone ? (
              <Button
                variant='outlined'
                color='secondary'
                startIcon={<EditOutlinedIcon />}
                sx={{ minWidth: '143px', p: (theme) => theme.spacing(1.5, 2) }}
                onClick={() =>
                  navigate(
                    matchedTaskManagement
                      ? `/task-management/question-creation/:taskId/item-set/:questionId`
                      : `/my-task/question-creation/:taskId/item-set/:questionId`,
                    {
                      params: {
                        taskId: task.id,
                        questionId: questionGroupId,
                      },
                      replace: true,
                    },
                  )
                }
              >
                Edit Paragraph
              </Button>
            ) : null}
          </Flex>
        </Flex>
      </StyledItemSetTableCell>
    </TableRow>
  );
}

function ItemSetSubQuestionTableRow(
  props: Readonly<{
    data: GetQuestionsByIdAndQuestionCreationTaskIdQuery['qctGetTaskQuestionsByIds'];
    order: number;
    questionGroupId: string;
  }>,
) {
  const { data, order, questionGroupId } = props;
  const row = transformQuestionsToQuestionRows(data)[0];
  const { roles, task } = useQuestionCreationTaskDetails();
  const navigate = useNavigate();
  const location = useLocation();
  const isTaskDone = task.currentWorkflowStep.code === WorkflowStepCode.QuestionCreationDone;
  const matchedTaskManagement = matchPath('/task-management/*', location.pathname);
  return (
    <TableRow
      onClick={() =>
        navigate(
          matchedTaskManagement
            ? `/task-management/question-creation/:taskId/item-set/:questionId/:subQuestionId`
            : `/my-task/question-creation/:taskId/item-set/:questionId/:subQuestionId`,
          {
            params: {
              taskId: task.id,
              questionId: questionGroupId,
              subQuestionId: row.id,
            },
            replace: true,
          },
        )
      }
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <StyledItemSetSubQuestionTableCell />
      <StyledItemSetSubQuestionTableCell
        sx={{
          p: (theme) => theme.spacing(3),
          minWidth: '76px',
        }}
      >
        {order}
      </StyledItemSetSubQuestionTableCell>
      <StyledItemSetSubQuestionTableCell
        sx={{
          p: (theme) => theme.spacing(3),
          minWidth: '246px',
        }}
      >
        <EllipsisText variant='body1'>{row.chapterName}</EllipsisText>
      </StyledItemSetSubQuestionTableCell>
      <StyledItemSetSubQuestionTableCell
        sx={{
          p: (theme) => theme.spacing(3),
          minWidth: '246px',
        }}
      >
        <EllipsisText variant='body1'>{row.topicName}</EllipsisText>
      </StyledItemSetSubQuestionTableCell>
      <StyledItemSetSubQuestionTableCell
        sx={{
          p: (theme) => theme.spacing(3),
          minWidth: '246px',
        }}
      >
        <EllipsisText variant='body1'>{row.subTopicName}</EllipsisText>
      </StyledItemSetSubQuestionTableCell>
      <StyledItemSetSubQuestionTableCell
        sx={{
          p: (theme) => theme.spacing(1.5, 2),
          width: '100px',
        }}
      >
        <EllipsisText variant='body1'>
          {row.calculation ? (
            <Chip
              label={<Text variant='captionSemibold'>{row.calculation}</Text>}
              size='small'
              sx={{
                backgroundColor: (theme) => theme.palette.grey[200],
                minWidth: '62px',
              }}
            />
          ) : (
            '-'
          )}
        </EllipsisText>
      </StyledItemSetSubQuestionTableCell>
      <StyledItemSetSubQuestionTableCell
        sx={{
          p: (theme) => theme.spacing(1.5, 2),
          width: '100px',
        }}
      >
        {row.difficulty ? (
          <DifficultyChip type={row.difficulty as 'easy' | 'normal' | 'hard'} />
        ) : (
          '-'
        )}
      </StyledItemSetSubQuestionTableCell>
      <StyledItemSetSubQuestionTableCell
        sx={{
          p: (theme) => theme.spacing(1.5),
          minWidth: '88px',
        }}
      >
        {roles.isAssignee && !isTaskDone ? (
          <Button
            variant='outlined'
            color='secondary'
            startIcon={<EditOutlinedIcon />}
            sx={{ p: (theme) => theme.spacing(1.5, 2), minWidth: '76px' }}
            size='small'
            onClick={() =>
              navigate(
                matchedTaskManagement
                  ? `/task-management/question-creation/:taskId/single/:questionId`
                  : `/my-task/question-creation/:taskId/item-set/:questionId/:subQuestionId`,
                {
                  params: {
                    taskId: task.id,
                    questionId: questionGroupId,
                    subQuestionId: row.id,
                  },
                  replace: true,
                },
              )
            }
          >
            Edit
          </Button>
        ) : null}
      </StyledItemSetSubQuestionTableCell>
    </TableRow>
  );
}
